// v3 tower, authored the way age-of-mcp writes it: local field helpers + the
// shared styleV3 part makers, instead of a flat wall of literals. A WATCHTOWER
// WITH AN OPEN TOP, read from above: one tapered octagonal stone shaft (the
// signature vertical) carrying an OPEN timber lookout platform — corner merlons
// only, no roof — so the camera looks straight down onto the deck's identity
// pair: a glowing FIRE brazier and a trade-red banner. A door and arrow slits
// face the street (+z). Ported 1:1 from age-of-mcp's v3/tower.ts.
import { defineModel, climb, styleV3, type Part } from "../../lib";

const { STONE, STONE_DK, STEEL, TIMBER, DOOR, FIRE, ROOF_TRADE, box, cyl, slab, poles } = styleV3;

const BASE    = 0.12;
const SHAFT_H = 2.4;

// The tapered octagonal stone shaft, `h` tall on the foundation.
function shaft(h: number): Part {
  return cyl(0.42, 0.52, h, 8, STONE, [0, BASE + h / 2, 0]);
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(1.84, 1.84)];
  if (s === 1) return [slab(1.84, 1.84), shaft(0.8), ...poles(1.1, 1.1, 1.0)];
  if (s === 2) return [slab(1.84, 1.84), shaft(1.7), ...poles(1.1, 1.1, 1.9)];

  const top = BASE + SHAFT_H;          // 2.52 — the shaft top
  const deck = top + 0.1;              // 2.62 — the open platform top
  const parts: Part[] = [
    slab(1.84, 1.84),
    shaft(SHAFT_H),
    // The OPEN lookout deck — a timber platform wider than the shaft.
    box(1.05, 0.1, 1.05, TIMBER, [0, top + 0.05, 0]),
    // Brazier (steel bowl + flame) — the glow read from above.
    cyl(0.12, 0.08, 0.14, 6, STEEL, [0.3, deck + 0.07, 0.3]),
    cyl(0, 0.09, 0.2, 5, FIRE, [0.3, deck + 0.24, 0.3]),
    // Banner pole flying trade red from the opposite corner.
    cyl(0.022, 0.022, 0.66, 4, TIMBER, [-0.3, deck + 0.33, -0.3]),
    box(0.36, 0.22, 0.03, ROOF_TRADE, [-0.14, deck + 0.5, -0.3]),
    // Door at the base and two arrow slits up the street face.
    box(0.26, 0.5, 0.06, DOOR, [0, BASE + 0.25, 0.49]),
    box(0.07, 0.26, 0.05, STEEL, [0, 1.4, 0.47]),
    box(0.07, 0.26, 0.05, STEEL, [0, 1.95, 0.45]),
  ];
  // Four stone merlons on the deck corners — the battlement silhouette.
  for (const [x, z] of [[-0.44, -0.44], [0.44, -0.44], [-0.44, 0.44], [0.44, 0.44]] as const) {
    parts.push(box(0.18, 0.24, 0.18, STONE_DK, [x, deck + 0.12, z]));
  }
  return parts;
}

export const towerV3 = defineModel({
  id: "tower-v3",
  tiles: { w: 2, h: 2 },
  batchId: "45-optimized",
  name: "Tower (v3)",
  tags: ["building","tower","low-poly","age-of-mcp","v3","military"],
  summary: "Low-poly tower.",
  description: "A tall stone watchtower with a crenellated top, trimmed with gold and gems — a grand prestige landmark.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
  stations: [
    { id: "face", position: [0, 0.14, 0.62], facing: Math.PI, description: "The tower face a sentry climbs." },
  ],
  interactions: [
    { id: "climb", label: "Climbing the tower", station: "face", variants: ["complete"], ...climb() },
  ],
});
